import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { preloadAllAssets, preloadPlayerCards } from "@/lib/preloader";
import AnimatedLoading from "./AnimatedLoading";

interface AssetPreloaderProps {
  children: React.ReactNode;
  disabled?: boolean;
}

// Set to true to mock loading state for styling purposes
const DEV_MOCK_LOADING = false;

export function AssetPreloader({
  children,
  disabled = false,
}: AssetPreloaderProps) {
  const [isPreloading, setIsPreloading] = React.useState(() => !disabled);
  const [showLoader, setShowLoader] = React.useState(() => !disabled);
  const [progress, setProgress] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const hideLoaderTimeout = React.useRef<number | undefined>(null);
  const hasPreloadedRef = React.useRef(false);
  const bgControllerRef = React.useRef<AbortController | null>(null);
  const bgStartedRef = React.useRef(false);

  React.useEffect(() => {
    if (hideLoaderTimeout.current) {
      window.clearTimeout(hideLoaderTimeout.current);
      hideLoaderTimeout.current = undefined;
    }

    if (disabled || hasPreloadedRef.current) {
      setIsPreloading(false);
      setShowLoader(false);
      return;
    }

    setIsPreloading(true);
    setShowLoader(true);
    setProgress(0);
    setTotal(0);
    let isCancelled = false;

    const startBackgroundCards = () => {
      if (bgStartedRef.current) return;
      bgStartedRef.current = true;
      const controller = new AbortController();
      bgControllerRef.current = controller;
      const run = () => {
        preloadPlayerCards({ concurrency: 4, signal: controller.signal }).catch(
          () => undefined,
        );
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ric = (window as any).requestIdleCallback as
        | undefined
        | ((cb: () => void, opts?: { timeout?: number }) => number);
      if (typeof ric === "function") {
        ric(run, { timeout: 2000 });
      } else {
        setTimeout(run, 0);
      }
    };

    const startPreload = async () => {
      try {
        await preloadAllAssets((loaded, totalAssets) => {
          if (isCancelled) {
            return;
          }
          setProgress(loaded);
          setTotal(totalAssets);
        });
        if (isCancelled) {
          return;
        }
        hasPreloadedRef.current = true;
        // Add small delay untuk smooth transition
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Only complete loading if not in mock mode
        if (!DEV_MOCK_LOADING) {
          setIsPreloading(false);
          // Begin background warmup of PLAYER_CARDS without blocking UI
          startBackgroundCards();
          hideLoaderTimeout.current = window.setTimeout(() => {
            if (!isCancelled) {
              setShowLoader(false);
            }
          }, 250);
        }
      } catch (error) {
        console.error("Preload error:", error);
        if (!DEV_MOCK_LOADING && !isCancelled) {
          setIsPreloading(false);
          hideLoaderTimeout.current = window.setTimeout(() => {
            if (!isCancelled) {
              setShowLoader(false);
            }
          }, 250);
        }
      }
    };

    startPreload();

    return () => {
      isCancelled = true;
      if (hideLoaderTimeout.current) {
        window.clearTimeout(hideLoaderTimeout.current);
        hideLoaderTimeout.current = undefined;
      }
      if (bgControllerRef.current) {
        bgControllerRef.current.abort();
        bgControllerRef.current = null;
      }
    };
  }, [disabled]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const rawPercentage = total > 0 ? Math.round((progress / total) * 100) : 0;
  const percentage = React.useMemo(
    () => (isPreloading ? Math.min(rawPercentage, 99) : 100),
    [isPreloading, rawPercentage],
  );

  return (
    <AnimatePresence mode="wait">
      {showLoader ? (
        <motion.div
          key="loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[1001] flex items-center justify-center bg-blue-950"
        >
          <div className="text-center space-y-6">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                duration: 0.25,
                type: "spring",
                bounce: 0.5,
              }}
            >
              <AnimatedLoading className="bg-transparent" />
            </motion.div>

            {/* Progress bar */}
            <div className="flex w-52 h-4 dark-green-panel border-2! bg-transparent! overflow-hidden">
              <motion.div
                className="h-full bg-[#068550]"
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 0.2 }}
              />
            </div>

            {/* Percentage */}
            <div>
              <motion.p
                className="-mt-2 text-white/80 font-semibold text-lg"
                key={percentage}
                initial={{ scale: 0.9, opacity: 0.5 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.15 }}
              >
                {percentage}%
              </motion.p>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
